import { useEffect, useRef } from 'react';

import Cookies from 'js-cookie';

type ZendeskFunction = (...args: unknown[]) => void;

declare global {
  interface Window {
    zE?: ZendeskFunction;
  }
}

interface UseZendeskAuthProps {
  isAuthenticated: boolean;
}

/**
 * Standalone function to logout the Zendesk widget.
 * Can be called directly without needing the hook context.
 * This is useful for the useLogout hook to ensure Zendesk is logged out
 * before the page redirects.
 */
export const logoutZendeskWidget = (): void => {
  if (typeof window !== 'undefined' && window.zE) {
    try {
      window.zE('messenger', 'logoutUser');
      console.log('Zendesk widget logged out');
    } catch (error) {
      console.error('Error logging out Zendesk widget:', error);
    }
  }
};

export const useZendeskAuth = ({ isAuthenticated }: UseZendeskAuthProps) => {
  const isInitializedRef = useRef(false);
  const previousAuthStateRef = useRef<boolean | null>(null);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const initializeZendeskWidget = async () => {
    const waitForZendesk = () => {
      return new Promise<void>(resolve => {
        if (window.zE) {
          resolve();
        } else {
          const checkZendesk = setInterval(() => {
            if (window.zE) {
              clearInterval(checkZendesk);
              resolve();
            }
          }, 100);

          setTimeout(() => {
            clearInterval(checkZendesk);
            resolve();
          }, 10000);
        }
      });
    };

    await waitForZendesk();

    if (window.zE) {
      try {
        window.zE(
          'messenger',
          'loginUser',
          function (callback: any) {
            try {
              const token = Cookies.get('ZendeskSecret');
              console.log('response ===> ', token);

              if (token) {
                console.log('Calling Zendesk callback with token:', token);
                callback(token);
              } else {
                callback(null);
                console.error('Failed to get valid Zendesk token');
              }
            } catch (error) {
              console.error('Error getting Zendesk token:', error);
              callback(null);
            }
          },
          function (error: any) {
            if (error === null) {
              console.log('✅ Zendesk authentication successful!');
            } else {
              console.error('❌ Zendesk Login Failed:', {
                message: error.message,
                reason: error.reason,
                type: error.type,
              });
            }
          }
        );
      } catch (error) {
        console.error('Error initializing Zendesk widget:', error);
      }
    } else {
      console.warn('Zendesk widget not available');
    }
  };

  const logoutZendeskWidget = () => {
    if (window.zE) {
      try {
        window.zE('messenger', 'logoutUser');
        console.log('Zendesk widget logged out');
      } catch (error) {
        console.error('Error logging out Zendesk widget:', error);
      }
    }
  };

  useEffect(() => {
    const handleZendeskAuth = async () => {
      const wasAuthenticated = previousAuthStateRef.current;
      const isNowAuthenticated = isAuthenticated;

      // Skip if this is the initial load and user is not authenticated
      if (wasAuthenticated === null && !isNowAuthenticated) {
        previousAuthStateRef.current = isNowAuthenticated;
        return;
      }

      // User just logged in
      if (!wasAuthenticated && isNowAuthenticated) {
        await initializeZendeskWidget();
        isInitializedRef.current = true;
      }

      // User just logged out
      if (wasAuthenticated && !isNowAuthenticated) {
        logoutZendeskWidget();
        isInitializedRef.current = false;
      }

      previousAuthStateRef.current = isNowAuthenticated;
    };

    // Only run if authentication state has changed or if user is authenticated and not initialized
    if (previousAuthStateRef.current !== isAuthenticated || (isAuthenticated && !isInitializedRef.current)) {
      handleZendeskAuth();
    }
  }, [isAuthenticated]);

  return {
    initializeZendeskWidget,
    logoutZendeskWidget,
    isInitialized: isInitializedRef.current,
  };
};
